import { useState, useMemo } from 'react';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Card,
    CardContent,
    Button,
    Stack,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import { Event, PackingOperationList, QueryItemTypesWithLocationsResponse } from '../schema';
import { getOneLink } from '../utils';
import ActiveEventRow from './ActiveEventRow';
import ListEmptyState from './ListEmptyState';

interface Props {
    events: Event[];
    activeOperation: PackingOperationList | null;
    itemTypesData: QueryItemTypesWithLocationsResponse | undefined;
    /** Map event ID -> trips (id + name) for trip link badges */
    eventIdToTrips?: Map<string, Array<{ id: string; name: string }>>;
}

type DateFilter = 'all' | 'current' | 'past' | 'future';
type SortOption = 'name-asc' | 'name-desc' | 'date-asc' | 'date-desc' | 'items-asc' | 'items-desc';

const TOUCH_TARGET_MIN = 48;

export default function ActiveEventsList({ events, activeOperation, itemTypesData, eventIdToTrips }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<DateFilter>('all');
    const [sortOption, setSortOption] = useState<SortOption>('name-asc');

    // Calculate item and trailer counts for each event
    const eventStats = useMemo(() => {
        const stats = new Map<string, { itemCount: number; trailerCount: number }>();

        if (itemTypesData?.itemTypes) {
            for (const itemType of itemTypesData.itemTypes) {
                const itemLocations = (itemType as any).itemLocations || [];
                for (const location of itemLocations) {
                    if (location.event?.id && (location.quantity || 0) > 0) {
                        const eventId = location.event.id;
                        const current = stats.get(eventId) || { itemCount: 0, trailerCount: 0 };
                        current.itemCount += location.quantity || 0;

                        // Count trailers (items where category name is "Trailers")
                        const categoryName = itemType.category?.name || '';
                        if (categoryName === 'Trailers') {
                            current.trailerCount += location.quantity || 0;
                        }

                        stats.set(eventId, current);
                    }
                }
            }
        }

        return stats;
    }, [itemTypesData]);

    // Filter and sort events
    const filteredAndSortedEvents = useMemo(() => {
        const now = new Date();
        let filtered = events;

        // Search filter
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(e => e.name.toLowerCase().includes(lowerSearch));
        }

        // Date filter
        if (dateFilter === 'current') {
            filtered = filtered.filter(e => {
                if (!e.startDate || !e.endDate) return false;
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);
                return start <= now && end >= now;
            });
        } else if (dateFilter === 'past') {
            filtered = filtered.filter(e => {
                if (!e.endDate) return false;
                return new Date(e.endDate) < now;
            });
        } else if (dateFilter === 'future') {
            filtered = filtered.filter(e => {
                if (!e.startDate) return false;
                return new Date(e.startDate) > now;
            });
        }

        // Sort
        filtered = [...filtered].sort((a, b) => {
            if (sortOption === 'name-asc') {
                return a.name.localeCompare(b.name);
            } else if (sortOption === 'name-desc') {
                return b.name.localeCompare(a.name);
            } else if (sortOption === 'date-asc') {
                const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
                const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
                return dateA - dateB;
            } else if (sortOption === 'date-desc') {
                const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
                const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
                return dateB - dateA;
            } else if (sortOption === 'items-asc') {
                const itemsA = eventStats.get(a.id)?.itemCount || 0;
                const itemsB = eventStats.get(b.id)?.itemCount || 0;
                return itemsA - itemsB;
            } else if (sortOption === 'items-desc') {
                const itemsA = eventStats.get(a.id)?.itemCount || 0;
                const itemsB = eventStats.get(b.id)?.itemCount || 0;
                return itemsB - itemsA;
            }
            return 0;
        });

        return filtered;
    }, [events, searchTerm, dateFilter, sortOption, eventStats]);

    const hasActiveFilters = searchTerm || dateFilter !== 'all' || sortOption !== 'name-asc';

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateFilter('all');
        setSortOption('name-asc');
    };

    return (
        <Box>
            {/* Filter/Search Bar */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={
                            isMobile
                                ? {
                                      '& .MuiButton-root': { minHeight: TOUCH_TARGET_MIN },
                                      '& .MuiTextField-root': { width: '100%' },
                                      '& .MuiFormControl-root': { width: '100%', minWidth: 0 },
                                  }
                                : undefined
                        }
                    >
                        {/* Search */}
                        <TextField
                            size={isMobile ? 'medium' : 'small'}
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ flex: 1 }}
                        />

                        {/* Date Filter */}
                        <FormControl size={isMobile ? 'medium' : 'small'} sx={{ minWidth: isMobile ? 0 : 150 }}>
                            <InputLabel>Date Filter</InputLabel>
                            <Select value={dateFilter} label="Date Filter" onChange={e => setDateFilter(e.target.value as DateFilter)}>
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="current">Current</MenuItem>
                                <MenuItem value="past">Past</MenuItem>
                                <MenuItem value="future">Future</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Sort */}
                        <FormControl size={isMobile ? 'medium' : 'small'} sx={{ minWidth: isMobile ? 0 : 150 }}>
                            <InputLabel>Sort</InputLabel>
                            <Select value={sortOption} label="Sort" onChange={e => setSortOption(e.target.value as SortOption)}>
                                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                                <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                                <MenuItem value="date-asc">Date (Oldest)</MenuItem>
                                <MenuItem value="date-desc">Date (Newest)</MenuItem>
                                <MenuItem value="items-asc">Items (Low to High)</MenuItem>
                                <MenuItem value="items-desc">Items (High to Low)</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <Button
                                size={isMobile ? 'medium' : 'small'}
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={isMobile ? { minHeight: TOUCH_TARGET_MIN } : undefined}
                            >
                                Clear
                            </Button>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Results Count */}
            {hasActiveFilters && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {filteredAndSortedEvents.length} of {events.length} events
                    </Typography>
                </Box>
            )}

            {/* Events List */}
            {filteredAndSortedEvents.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredAndSortedEvents.map(event => {
                        const stats = eventStats.get(event.id) || { itemCount: 0, trailerCount: 0 };
                        const group = getOneLink((event as Event & { group?: unknown }).group);
                        const groupName = group && typeof group === 'object' && 'name' in group ? (group as { name: string }).name : null;
                        const trips = eventIdToTrips?.get(event.id) ?? [];
                        return (
                            <ActiveEventRow
                                key={event.id}
                                event={event}
                                itemCount={stats.itemCount}
                                trailerCount={stats.trailerCount}
                                groupName={groupName}
                                trips={trips}
                                activeOperation={activeOperation}
                                itemTypesData={itemTypesData}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Card sx={{ boxShadow: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <ListEmptyState
                            icon={<EventIcon sx={{ fontSize: 48 }} />}
                            message={hasActiveFilters ? 'No events match your filters' : 'All items are at HQ'}
                            subtext={hasActiveFilters ? 'Try adjusting your filters' : 'No events currently have inventory checked out'}
                        />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
